/**
 * 生成済みレポート HTML を、既存タブ UI の中に「形式を揃えて」埋め込む。
 *
 * どの種別の資料が存在するかは deploy 供給（/api/meeting-board/reports/:type）。
 * ここは種別を文字列として受け取るだけで、意味は解釈しない。
 *
 * 方式:
 *  - レポート側の見出し(.head)と自前タブ(.tabs)は CSS で隠し、CH 側のUIで描画する
 *  - レポート内ペイン(#pane-pN)の切替は postMessage で行う（iframe は allow-scripts のみ）
 *  - 日付ナビで過去分に遡れる
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, ChevronLeft, ChevronRight, FileText, Info } from 'lucide-react'
import { Badge } from '@/components/Badge'
import { useBoardReport, useBoardReportDates } from '@/hooks/useMeetingBoard'
import { cn } from '@/lib/utils'

/** 資料種別。deploy が配るディレクトリ名そのままで、UI は意味を持たない。 */
export type BoardReportType = string

interface ReportDateItem {
  date: string
  revision: number
  createdAt: string
  bytes: number
}

interface ReportDocument extends ReportDateItem {
  reportType: string
  html: string
}

function useReportDates(type: BoardReportType) {
  return useBoardReportDates<{ items: ReportDateItem[] }>(type)
}

function useReportDocument(type: BoardReportType, date: string | null) {
  return useBoardReport<{ document: ReportDocument }>(type, date)
}

/** レポート側HTMLに、CHのカード内へ溶け込ませるCSSとペイン切替スクリプトを注入する */
function prepareHtml(html: string, initialPane: string | null): string {
  const injected = `
<style>
  /* LOCAL-ONLY: 埋め込み用の見た目調整 */
  html, body { background: transparent !important; }
  body { padding: 0 !important; }
  .sheet { max-width: none !important; border: 0 !important; border-radius: 0 !important;
           padding: 0 !important; box-shadow: none !important; background: transparent !important; }
  .sheet > .head, .sheet > #tabs, .sheet > .tabs { display: none !important; }
  .sheet > .foot { margin-top: 24px; }
  /* 日次レポート側（.wrap 構造）: 幅を器に合わせ、CH側と重複する大見出しだけ隠す */
  .wrap { max-width: none !important; padding: 0 !important; }
  .rpt-head { padding-top: 0 !important; }
  .rpt-head h1 { display: none !important; }
  /* モバイル要件（web-ui-spec §3.5.4）: 幅超過は要素単位の横スクロールに閉じ込める。
     資料側の table は .twrap に入っていないものがあるため、ここで一律に効かせる。 */
  .twrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
  table { max-width: 100%; }
</style>
<script>
  (function () {
    var showPane = function (pane) {
      var panes = document.querySelectorAll('.pane');
      var found = false;
      for (var i = 0; i < panes.length; i++) {
        var on = panes[i].id === 'pane-' + pane;
        panes[i].classList.toggle('on', on);
        if (on) found = true;
      }
      return found;
    };
    var report = function () {
      var tabs = [].map.call(document.querySelectorAll('#tabs .tab'), function (t) {
        var pane = t.getAttribute('data-pane');
        var body = document.getElementById('pane-' + pane);
        return {
          pane: pane,
          label: (t.textContent || '').replace(/^\\s*\\d+\\s*/, '').trim(),
          // 中身が実質空の章（1行だけのプレースホルダ等）を親側で判定するため
          textLength: body ? (body.textContent || '').trim().length : 0,
        };
      });
      var active = document.querySelector('.pane.on');
      parent.postMessage({ type: 'ch-report-ready', tabs: tabs,
        activePane: active ? active.id.replace('pane-', '') : null,
        height: document.documentElement.scrollHeight }, '*');
    };
    window.addEventListener('message', function (e) {
      var data = e.data || {};
      if (data.type === 'ch-report-show-pane') {
        showPane(data.pane);
        parent.postMessage({ type: 'ch-report-height', height: document.documentElement.scrollHeight }, '*');
      }
    });
    // 既定の章: URL指定があればそれ。無ければ資料側の既定。
    // ただし資料側の既定が実質空（プレースホルダ1行だけ等）なら、中身のある最初の章にする。
    var pickInitialPane = function () {
      var requested = ${initialPane ? JSON.stringify(initialPane) : 'null'};
      if (requested && document.getElementById('pane-' + requested)) return requested;
      var panes = [].slice.call(document.querySelectorAll('.pane'));
      var textOf = function (el) { return (el.textContent || '').trim().length; };
      var current = document.querySelector('.pane.on');
      if (current && textOf(current) >= ${EMPTY_PANE_TEXT_THRESHOLD}) return current.id.replace('pane-', '');
      for (var i = 0; i < panes.length; i++) {
        if (textOf(panes[i]) >= ${EMPTY_PANE_TEXT_THRESHOLD}) return panes[i].id.replace('pane-', '');
      }
      return current ? current.id.replace('pane-', '') : null;
    };
    var boot = function () {
      var initial = pickInitialPane();
      if (initial) showPane(initial);
      report();
      var ro = new ResizeObserver(function () {
        parent.postMessage({ type: 'ch-report-height', height: document.documentElement.scrollHeight }, '*');
      });
      ro.observe(document.documentElement);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  })();
</script>`
  return html.includes('</body>')
    ? html.replace('</body>', `${injected}\n</body>`)
    : html + injected
}

/** 最新資料の日付から経過日数（JST日付文字列同士の素朴な差分で足りる） */
function daysSince(date: string): number | null {
  const parsed = new Date(`${date}T00:00:00+09:00`)
  if (Number.isNaN(parsed.getTime())) return null
  return Math.floor((Date.now() - parsed.getTime()) / 86_400_000)
}

function formatDateLabel(date: string): string {
  const [, m, d] = date.split('-')
  return `${Number(m)}/${Number(d)}`
}

function formatDateTime(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '未確認'
  return parsed.toLocaleString('ja-JP', {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/** 実質空の章（プレースホルダ1行だけ等）とみなす文字数のしきい値 */
const EMPTY_PANE_TEXT_THRESHOLD = 150

export function BoardReportViewer({
  reportType,
  title,
  description,
  dateUnitLabel,
  meetingWeekOf,
  staleAfterDays,
}: {
  reportType: BoardReportType
  title: string
  description: string
  /** 日付ナビの単位表記（例: 「の週」 / 「分」） */
  dateUnitLabel: string
  /** 会議snapshotの週（資料の週と食い違うときに注意書きを出すため） */
  meetingWeekOf?: string
  /** 最新の資料がこの日数以上古いときに鮮度警告を出す（日報=2 / 週次=8） */
  staleAfterDays: number
}) {
  const datesQuery = useReportDates(reportType)
  const items = datesQuery.data?.items ?? []
  // 日付と章はURLに持たせる（投影モードやリロードで選択が飛ばない・URLで共有できる）
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedDate = searchParams.get('date')
  const activeDate = (selectedDate && items.some((item) => item.date === selectedDate) ? selectedDate : null)
    ?? items[0]?.date
    ?? null
  const documentQuery = useReportDocument(reportType, activeDate)
  const doc = documentQuery.data?.document

  const [panes, setPanes] = useState<{ pane: string; label: string; textLength: number }[]>([])
  const [frameHeight, setFrameHeight] = useState(1200)
  const frameRef = useRef<HTMLIFrameElement>(null)

  const requestedPane = searchParams.get('pane')
  // 既定章の判定は資料側(iframe)で完結させ、その結果を受け取って章タブの選択状態に反映する
  const [reportedPane, setReportedPane] = useState<string | null>(null)
  const activePane = requestedPane && panes.some((pane) => pane.pane === requestedPane)
    ? requestedPane
    : reportedPane

  const setSelectedDate = (date: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('date', date)
    next.delete('pane')
    setSearchParams(next, { replace: true })
  }

  // 600KB級のHTMLは srcdoc 属性に載せると描画されないことがあるため Blob URL で渡す
  const srcDoc = useMemo(
    () => (doc ? prepareHtml(doc.html, requestedPane) : null),
    // doc の他フィールドが変わっても本文が同じなら作り直さない
    [doc?.html],
  )
  const [frameSrc, setFrameSrc] = useState<string | null>(null)
  useEffect(() => {
    if (!srcDoc) {
      setFrameSrc(null)
      return
    }
    // charset を明示しないと <meta charset> の無い資料（日報テンプレ）が文字化けする
    const objectUrl = URL.createObjectURL(new Blob([srcDoc], { type: 'text/html;charset=utf-8' }))
    setFrameSrc(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [srcDoc])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // 送信元を照合する。sandbox iframe は opaque origin なので origin では判別できず、
      // 照合しないと同じウィンドウに届く任意のフレームが章ラベルや高さを操作できてしまう。
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return
      const data = event.data as {
        type?: string
        tabs?: { pane: string; label: string; textLength: number }[]
        activePane?: string | null
        height?: number
      }
      if (data?.type === 'ch-report-ready') {
        // 「前回の確認」は資料側が空欄のまま（ダッシュボード側の“前回会議のふりかえり”が受け皿）なので出さない
        setPanes((data.tabs ?? []).filter((tab) => !tab.label.includes('前回の確認')))
        setReportedPane(data.activePane ?? null)
        if (data.height) setFrameHeight(data.height + 32)
      }
      if (data?.type === 'ch-report-height' && data.height) setFrameHeight(data.height + 32)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // 章の切替時に、表示すべき章をiframeへ伝える
  useEffect(() => {
    if (!activePane) return
    frameRef.current?.contentWindow?.postMessage({ type: 'ch-report-show-pane', pane: activePane }, '*')
  }, [activePane, srcDoc])

  // 投影モードの出入り等でiframeが作り直されたとき、選択中の章を復元する
  const activePaneRef = useRef<string | null>(null)
  activePaneRef.current = activePane
  const restorePaneOnLoad = () => {
    const pane = activePaneRef.current
    if (!pane) return
    frameRef.current?.contentWindow?.postMessage({ type: 'ch-report-show-pane', pane }, '*')
  }

  const showPane = (pane: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('pane', pane)
    setSearchParams(next, { replace: true })
  }

  // 鮮度は「保存されている最新の資料」で判定する（過去分を見ているときも警告は出したままにする）
  const latestDate = items[0]?.date ?? null
  const latestAgeDays = latestDate ? daysSince(latestDate) : null
  const stale = latestDate !== null && latestAgeDays !== null && latestAgeDays >= staleAfterDays
    ? { days: latestAgeDays, latestDate }
    : null

  const index = activeDate ? items.findIndex((item) => item.date === activeDate) : -1
  const olderDate = index >= 0 && index + 1 < items.length ? items[index + 1]!.date : null
  const newerDate = index > 0 ? items[index - 1]!.date : null
  const isLatest = index === 0

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-semibold">
            <FileText className="h-4 w-4 text-primary" aria-hidden />
            {title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => olderDate && setSelectedDate(olderDate)}
            disabled={!olderDate}
            aria-label="前の資料"
            className="inline-flex min-h-10 items-center gap-1 rounded-md border bg-card px-2 py-2 text-sm font-medium hover:bg-muted disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <select
            value={activeDate ?? ''}
            onChange={(event) => setSelectedDate(event.target.value)}
            aria-label="資料の日付"
            className="min-h-10 rounded-md border bg-card px-3 py-2 text-sm font-medium tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {items.map((item) => (
              <option key={item.date} value={item.date}>
                {item.date}{dateUnitLabel}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => newerDate && setSelectedDate(newerDate)}
            disabled={!newerDate}
            aria-label="次の資料"
            className="inline-flex min-h-10 items-center gap-1 rounded-md border bg-card px-2 py-2 text-sm font-medium hover:bg-muted disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
          {!isLatest && items[0] && (
            <button
              type="button"
              onClick={() => setSelectedDate(items[0]!.date)}
              className="inline-flex min-h-10 items-center rounded-md border bg-card px-3 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              最新へ
            </button>
          )}
        </div>
      </div>

      {activeDate && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {/* 「資料の」を明記して、画面上部の会議snapshotの週と混同しないようにする */}
          <span className="font-medium tabular-nums">
            <span className="text-muted-foreground">資料の</span>{formatDateLabel(activeDate)}{dateUnitLabel}
          </span>
          <Badge tone={isLatest ? 'success' : 'muted'} className="rounded-md">
            {isLatest ? '最新' : '過去分'}
          </Badge>
          {doc && (
            <>
              <span className="tabular-nums text-muted-foreground">資料保存日時 {formatDateTime(doc.createdAt)}</span>
              <span className="tabular-nums text-muted-foreground">改訂 {doc.revision}</span>
            </>
          )}
        </div>
      )}

      {/* 鮮度警告: 最新の資料が想定より古いとき（生成の失敗・上げ忘れ・月初スキップ）に気付けるようにする */}
      {stale !== null && (
        <p className="flex items-start gap-2 rounded-md border border-yellow-600/40 bg-yellow-600/10 px-3 py-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" aria-hidden />
          <span>
            <span className="font-medium">最新の資料が {stale.days}日前（{formatDateLabel(stale.latestDate)}{dateUnitLabel}）です。</span>
            {' '}資料の更新が止まっている可能性があります（生成の失敗・アップロード漏れ・月初のスキップなど）。数字を判断に使う前に更新状況を確認してください。
          </span>
        </p>
      )}

      {/* 会議snapshotの週と資料の週が違うときは、その場で気付けるようにする */}
      {activeDate && meetingWeekOf && meetingWeekOf !== activeDate && (
        <p className="flex items-start gap-2 rounded-md border border-yellow-600/30 bg-yellow-600/5 px-3 py-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" aria-hidden />
          <span>
            この画面の会議snapshotは <span className="font-medium tabular-nums text-foreground">{formatDateLabel(meetingWeekOf)}の週</span>、
            表示中の資料は <span className="font-medium tabular-nums text-foreground">{formatDateLabel(activeDate)}{dateUnitLabel}</span> です。別々に更新されるため週がずれることがあります。
          </span>
        </p>
      )}

      {panes.length > 0 && (
        <div className="flex w-fit flex-wrap gap-1 rounded-md border bg-card p-1" aria-label="資料の章">
          {panes.map((pane) => (
            <button
              key={pane.pane}
              type="button"
              aria-pressed={activePane === pane.pane}
              onClick={() => showPane(pane.pane)}
              className={cn(
                'whitespace-nowrap rounded-sm px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-px',
                activePane === pane.pane ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {pane.label}
            </button>
          ))}
        </div>
      )}

      {datesQuery.isLoading || documentQuery.isLoading ? (
        <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground shadow-sm">読み込み中...</div>
      ) : datesQuery.isError || documentQuery.isError ? (
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <p className="font-semibold">資料を取得できませんでした</p>
          <p className="mt-1 text-sm text-muted-foreground">接続状態を確認してから再度お試しください。</p>
        </div>
      ) : !srcDoc ? (
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <p className="font-semibold">この日付の資料はまだありません</p>
          <p className="mt-1 text-sm text-muted-foreground">生成済みの資料が保存されると表示されます。</p>
        </div>
      ) : (
        // 狭い画面では器の余白を詰めて資料の実効幅を稼ぐ（390px で +48px 相当）
        <div className="overflow-hidden rounded-lg border bg-card p-2 shadow-sm sm:p-6">
          <iframe
            ref={frameRef}
            title={title}
            sandbox="allow-scripts"
            onLoad={restorePaneOnLoad}
            src={frameSrc ?? undefined}
            className="w-full border-0"
            style={{ height: frameHeight }}
          />
        </div>
      )}
    </div>
  )
}
